import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, X, AlertCircle, HardHat, Search, AlertTriangle } from 'lucide-react'
import api from '../../services/api'

const TL_COLOUR = { green: 'var(--green)', amber: 'var(--amber)', red: 'var(--red)' }
const TL_BG = { green: '#f0fdf4', amber: '#fffbeb', red: '#fef2f2' }
const TL_LABEL = { green: 'Compliant', amber: 'Action Required', red: 'Non-Compliant' }
const TL_BADGE = { green: 'badge-low', amber: 'badge-high', red: 'badge-critical' }

const AU_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT']
const WORK_ACTIVITIES = [
  'Electrical', 'Plumbing', 'Civil / Earthworks', 'Roofing', 'Scaffolding',
  'Demolition', 'Asbestos Removal', 'Confined Space', 'Working at Heights',
  'Rigging / Crane', 'Traffic Management', 'Landscaping', 'Cleaning',
  'Security', 'IT / Communications', 'Mechanical', 'HVAC', 'Other',
]

function TrafficDot({ light }: { light: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0" style={{ background: TL_COLOUR[light as keyof typeof TL_COLOUR] || '#9ca3af' }} />
      <span className="text-xs font-medium" style={{ color: TL_COLOUR[light as keyof typeof TL_COLOUR] }}>
        {TL_LABEL[light as keyof typeof TL_LABEL] || light}
      </span>
    </span>
  )
}

function expiryStyle(date: string | null) {
  if (!date) return { label: '—', color: 'var(--text-muted)' }
  const d = new Date(date)
  const today = new Date()
  const in30 = new Date(today.getTime() + 30 * 86400000)
  if (d < today) return { label: `Expired ${d.toLocaleDateString('en-AU')}`, color: 'var(--red)' }
  if (d < in30) return { label: `Exp ${d.toLocaleDateString('en-AU')}`, color: 'var(--amber)' }
  return { label: d.toLocaleDateString('en-AU'), color: 'var(--text-secondary)' }
}

export default function ContractorsList() {
  const [data, setData] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterTL, setFilterTL] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const navigate = useNavigate()

  const load = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (search) params.set('search', search)
      if (filterTL) params.set('traffic_light', filterTL)
      const { data: res } = await api.get(`/contractors?${params}`)
      setData(res.data || [])
      setTotal(res.total || 0)
    } catch {}
    setLoading(false)
  }

  useEffect(() => { load() }, [search, filterTL])

  const redCount = data.filter(c => c.traffic_light === 'red').length
  const amberCount = data.filter(c => c.traffic_light === 'amber').length

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Contractors</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>{total} registered contractors</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn btn-primary"><Plus size={16} /> Add Contractor</button>
      </div>

      {/* Alert banners */}
      {redCount > 0 && (
        <div className="flex items-center gap-2 p-3 rounded-lg text-sm" style={{ background: '#fef2f2', color: 'var(--red)', border: '1px solid var(--red)' }}>
          <AlertCircle size={16} className="flex-shrink-0" />
          <strong>{redCount} contractor{redCount > 1 ? 's' : ''} non-compliant</strong> — do not permit work until issues are resolved
        </div>
      )}
      {amberCount > 0 && (
        <div className="flex items-center gap-2 p-3 rounded-lg text-sm" style={{ background: '#fffbeb', color: 'var(--amber)', border: '1px solid var(--amber)' }}>
          <AlertTriangle size={16} className="flex-shrink-0" />
          <strong>{amberCount} contractor{amberCount > 1 ? 's' : ''} require attention</strong> — review compliance documents
        </div>
      )}

      {/* Filters */}
      <div className="card p-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search contractors..."
            style={{ paddingLeft: 34 }}
          />
        </div>
        <select className="w-auto" value={filterTL} onChange={e => setFilterTL(e.target.value)}>
          <option value="">All statuses</option>
          <option value="green">Compliant</option>
          <option value="amber">Action Required</option>
          <option value="red">Non-Compliant</option>
        </select>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-4">
        {(['green', 'amber', 'red'] as const).map(tl => (
          <button
            key={tl}
            onClick={() => setFilterTL(filterTL === tl ? '' : tl)}
            className="card p-4 text-left transition-all hover:shadow-md"
            style={{ borderLeft: `4px solid ${TL_COLOUR[tl]}`, background: filterTL === tl ? TL_BG[tl] : undefined, cursor: 'pointer' }}
          >
            <div className="text-2xl font-bold" style={{ color: TL_COLOUR[tl] }}>
              {data.filter(c => c.traffic_light === tl).length}
            </div>
            <div className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>{TL_LABEL[tl]}</div>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>Loading...</div>
      ) : data.length === 0 ? (
        <div className="card text-center py-16">
          <HardHat size={40} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
          <p className="font-medium" style={{ color: 'var(--text)' }}>No contractors found</p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Register contractor companies to manage compliance</p>
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Company</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Status</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Workers</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>PL Insurance</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Workers Comp</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Induction</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Pre-qual Score</th>
              </tr>
            </thead>
            <tbody>
              {data.map(c => {
                const pl = expiryStyle(c.insurance_public_liability_expiry)
                const wc = expiryStyle(c.insurance_wc_expiry)
                return (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/contractors/${c.id}`)}
                    style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium" style={{ color: 'var(--text)' }}>{c.company_name}</div>
                      {c.abn && <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>ABN {c.abn}</div>}
                    </td>
                    <td className="px-4 py-3"><TrafficDot light={c.traffic_light} /></td>
                    <td className="px-4 py-3 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{c.worker_count || 0}</td>
                    <td className="px-4 py-3 text-xs font-medium" style={{ color: pl.color }}>{pl.label}</td>
                    <td className="px-4 py-3 text-xs font-medium" style={{ color: wc.color }}>{wc.label}</td>
                    <td className="px-4 py-3">
                      <span className={`badge ${c.induction_current ? 'badge-low' : 'badge-critical'}`}>
                        {c.induction_current ? 'Current' : 'Overdue'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {c.prequalification_score != null ? (
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 rounded-full bg-gray-200 w-16 overflow-hidden">
                            <div className="h-full rounded-full" style={{
                              width: `${c.prequalification_score}%`,
                              background: c.prequalification_score >= 80 ? 'var(--green)' : c.prequalification_score >= 60 ? 'var(--amber)' : 'var(--red)',
                            }} />
                          </div>
                          <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{c.prequalification_score}%</span>
                        </div>
                      ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && <AddContractorModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load() }} />}
    </div>
  )
}

// ─── Add Contractor Modal ─────────────────────────────────────────────────────

function AddContractorModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    company_name: '', abn: '', primary_contact: '', email: '', mobile: '',
    activities: [] as string[],
    licence_status: 'active',
    licence_expires_at: '',
    insurance_public_liability_expiry: '',
    insurance_wc_expiry: '',
    induction_current: false,
    prequalification_score: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const toggleActivity = (act: string) => {
    setForm(f => ({
      ...f,
      activities: f.activities.includes(act) ? f.activities.filter(a => a !== act) : [...f.activities, act],
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.company_name) { setError('Company name is required'); return }
    setSaving(true)
    setError('')
    try {
      await api.post('/contractors', {
        ...form,
        prequalification_score: form.prequalification_score ? parseFloat(form.prequalification_score) : undefined,
        licence_expires_at: form.licence_expires_at || undefined,
        insurance_public_liability_expiry: form.insurance_public_liability_expiry || undefined,
        insurance_wc_expiry: form.insurance_wc_expiry || undefined,
      })
      onSaved()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save')
    }
    setSaving(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="w-full max-w-2xl rounded-2xl shadow-xl overflow-y-auto" style={{ background: 'var(--white)', maxHeight: '90vh' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10" style={{ borderColor: 'var(--border)' }}>
          <h2 className="font-semibold text-lg" style={{ color: 'var(--text)' }}>Add Contractor</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          {/* Company info */}
          <div>
            <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Company Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Company Name *</label>
                <input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} placeholder="e.g. Smith Electrical Pty Ltd" required />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>ABN</label>
                <input value={form.abn} onChange={e => setForm(f => ({ ...f, abn: e.target.value }))} placeholder="12 345 678 901" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Primary Contact</label>
                <input value={form.primary_contact} onChange={e => setForm(f => ({ ...f, primary_contact: e.target.value }))} placeholder="Full name" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Email</label>
                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="contact@contractor.com.au" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Mobile</label>
                <input value={form.mobile} onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))} placeholder="04XX XXX XXX" />
              </div>
            </div>
          </div>

          {/* Work activities */}
          <div>
            <label className="text-sm font-semibold mb-2 block" style={{ color: 'var(--text-secondary)' }}>Work Activities</label>
            <div className="flex flex-wrap gap-2">
              {WORK_ACTIVITIES.map(act => (
                <button
                  key={act}
                  type="button"
                  onClick={() => toggleActivity(act)}
                  className="px-3 py-1 rounded-lg text-xs font-medium border transition-all"
                  style={{
                    background: form.activities.includes(act) ? 'var(--accent)' : 'var(--bg)',
                    color: form.activities.includes(act) ? 'white' : 'var(--text-secondary)',
                    borderColor: form.activities.includes(act) ? 'var(--accent)' : 'var(--border)',
                    cursor: 'pointer',
                  }}
                >
                  {act}
                </button>
              ))}
            </div>
          </div>

          {/* Compliance */}
          <div>
            <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Compliance & Insurance</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Licence Status</label>
                <select value={form.licence_status} onChange={e => setForm(f => ({ ...f, licence_status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="pending">Pending Verification</option>
                  <option value="suspended">Suspended</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Licence Expiry</label>
                <input type="date" value={form.licence_expires_at} onChange={e => setForm(f => ({ ...f, licence_expires_at: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>PL Insurance Expiry</label>
                <input type="date" value={form.insurance_public_liability_expiry} onChange={e => setForm(f => ({ ...f, insurance_public_liability_expiry: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Workers Comp Expiry</label>
                <input type="date" value={form.insurance_wc_expiry} onChange={e => setForm(f => ({ ...f, insurance_wc_expiry: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Pre-qual Score (0–100)</label>
                <input type="number" min="0" max="100" value={form.prequalification_score} onChange={e => setForm(f => ({ ...f, prequalification_score: e.target.value }))} placeholder="e.g. 85" />
              </div>
              <div className="flex items-center gap-3 pt-5">
                <input type="checkbox" id="induction" checked={form.induction_current} onChange={e => setForm(f => ({ ...f, induction_current: e.target.checked }))} style={{ width: 16, height: 16 }} />
                <label htmlFor="induction" className="text-sm" style={{ color: 'var(--text-secondary)' }}>Site induction current</label>
              </div>
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 text-sm p-3 rounded-lg" style={{ background: '#fef2f2', color: 'var(--red)' }}>
              <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />{error}
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Add Contractor'}</button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  )
}
